import { actionTypes, getNodeId } from '../actions/drive'
import { IDriveFolderContents, IDriveNode, IDriveState } from '../../config/driveTypes'

export const initialState: IDriveState = {
  roots: [],
  rootsLoading: false,
  hasLoadedRoots: false,
  contentsByFolderId: {},
  searchIndex: null,
  searchIndexLoading: false,
  searchIndexLoadedAt: null,
  searchIndexTruncated: false,
}

const emptyContents: IDriveFolderContents = {
  items: [],
  nextCursor: null,
  isLoading: false,
  hasLoadedOnce: false,
  folderName: null,
}

const driveReducer = (state = initialState, action: any): IDriveState => {
  switch (action.type) {
    case actionTypes.SET_ROOTS_LOADING:
      return { ...state, rootsLoading: action.isLoading }

    case actionTypes.SET_ROOTS:
      return { ...state, roots: action.roots, hasLoadedRoots: true }

    case actionTypes.ADD_ROOT:
      return { ...state, roots: [action.root, ...state.roots], searchIndexLoadedAt: null }

    case actionTypes.REMOVE_ROOT:
      return {
        ...state,
        roots: state.roots.filter((node) => getNodeId(node) !== action.folderId),
        searchIndexLoadedAt: null,
      }

    case actionTypes.SET_SEARCH_INDEX_LOADING:
      return { ...state, searchIndexLoading: action.isLoading }

    case actionTypes.SET_SEARCH_INDEX:
      return {
        ...state,
        searchIndex: action.items,
        searchIndexTruncated: action.truncated,
        searchIndexLoadedAt: action.loadedAt,
        searchIndexLoading: false,
      }

    case actionTypes.SET_CONTENTS_LOADING: {
      const current = state.contentsByFolderId[action.folderId] || emptyContents
      return {
        ...state,
        contentsByFolderId: {
          ...state.contentsByFolderId,
          [action.folderId]: { ...current, isLoading: action.isLoading },
        },
      }
    }

    case actionTypes.SET_FOLDER_CONTENTS: {
      const current = state.contentsByFolderId[action.folderId] || emptyContents
      const items = action.append ? [...current.items, ...action.items] : action.items
      return {
        ...state,
        contentsByFolderId: {
          ...state.contentsByFolderId,
          [action.folderId]: {
            items,
            nextCursor: action.nextCursor,
            isLoading: false,
            hasLoadedOnce: true,
            folderName: action.folderName ?? current.folderName ?? null,
          },
        },
      }
    }

    case actionTypes.ADD_ITEM: {
      const current = state.contentsByFolderId[action.folderId] || emptyContents
      return {
        ...state,
        contentsByFolderId: {
          ...state.contentsByFolderId,
          [action.folderId]: { ...current, items: [action.item, ...current.items] },
        },
        searchIndexLoadedAt: null,
      }
    }

    case actionTypes.REMOVE_ITEM: {
      const current = state.contentsByFolderId[action.folderId]
      if (!current) return state
      return {
        ...state,
        contentsByFolderId: {
          ...state.contentsByFolderId,
          [action.folderId]: {
            ...current,
            items: current.items.filter((node) => getNodeId(node) !== action.fileId),
          },
        },
        searchIndexLoadedAt: null,
      }
    }

    case actionTypes.MARK_ITEM_VIEWED: {
      const current = state.contentsByFolderId[action.folderId]
      if (!current) return state
      return {
        ...state,
        contentsByFolderId: {
          ...state.contentsByFolderId,
          [action.folderId]: {
            ...current,
            items: current.items.map((node) =>
              getNodeId(node) === action.fileId
                ? { ...node, is_viewed: true, view_count: (node.view_count || 0) + 1, last_viewed_ts: action.viewedAt }
                : node
            ),
          },
        },
      }
    }

    // File/link rename: merge only the fields PUT /files/{id} actually returns (name/url/mime/size/updated_*).
    // Unlike RENAME_NODE this doesn't touch access_level/is_viewed/view_count, which that response omits.
    case actionTypes.UPDATE_ITEM_META: {
      const current = state.contentsByFolderId[action.folderId]
      if (!current) return state
      return {
        ...state,
        contentsByFolderId: {
          ...state.contentsByFolderId,
          [action.folderId]: {
            ...current,
            items: current.items.map((node) =>
              getNodeId(node) === action.fileId
                ? {
                    ...node,
                    name: action.node.name,
                    url: action.node.url,
                    mime_type: action.node.mime_type,
                    size_bytes: action.node.size_bytes,
                    updated_by: action.node.updated_by,
                    updated_at_ts: action.node.updated_at_ts,
                  }
                : node
            ),
          },
        },
        searchIndexLoadedAt: null,
      }
    }

    case actionTypes.RENAME_NODE: {
      const renamedId = getNodeId(action.node)
      const rename = (node: IDriveNode) =>
        getNodeId(node) === renamedId ? { ...node, name: action.node.name, icon: action.node.icon } : node

      const roots = state.roots.map(rename)
      const contentsByFolderId: IDriveState['contentsByFolderId'] = {}
      Object.keys(state.contentsByFolderId).forEach((fid) => {
        const bucket = state.contentsByFolderId[fid]
        contentsByFolderId[fid] = { ...bucket, items: bucket.items.map(rename) }
      })
      return { ...state, roots, contentsByFolderId, searchIndexLoadedAt: null }
    }

    default:
      return state
  }
}

export default driveReducer
